/**
 * Шаблоны, повторяющиеся задачи и промежуточные дела — три подсистемы, которые
 * порождают или дробят задачи.
 *
 * Шаблон живёт сам по себе (`/v2/tasks/templates`) и по `instantiate` создаёт
 * задачу. Повтор привязан к задаче-образцу (`/v2/tasks/{id}/recurrence`), а
 * список всех правил и история запусков — на уровне группы
 * (`/v2/tasks/recurrences`). Промежуточное дело — подзадача внутри задачи со
 * своим таймером: старт, пауза, продолжение, завершение и правка потраченного.
 */
import { http } from '@/shared/api';
import type { SchemaV2 } from '@/shared/api';

export type TaskTemplateV2 =
  SchemaV2<'internal_tasks_handler.templateResponse'>;
export type TaskTemplateWriteV2 =
  SchemaV2<'internal_tasks_handler.templateWriteRequest'>;
export type SaveAsTemplateV2 =
  SchemaV2<'internal_tasks_handler.saveAsTemplateRequest'>;
export type RecurrenceV2 =
  SchemaV2<'internal_tasks_handler.recurrenceResponse'>;
export type RecurrenceCreateV2 =
  SchemaV2<'internal_tasks_handler.recurrenceCreateRequest'>;
export type RecurrenceUpdateV2 =
  SchemaV2<'internal_tasks_handler.recurrenceUpdateRequest'>;
export type RecurrenceRunV2 =
  SchemaV2<'internal_tasks_handler.recurrenceRunResponse'>;
export type IntermediateV2 =
  SchemaV2<'internal_tasks_handler.intermediateResponse'>;
export type IntermediateCreateV2 =
  SchemaV2<'internal_tasks_handler.intermediateCreateRequest'>;
export type IntermediateTimeV2 =
  SchemaV2<'internal_tasks_handler.intermediateTimeRequest'>;

const task = (id: string) => `/v2/tasks/${encodeURIComponent(id)}`;

/* --------------------------------------------------------- шаблоны --- */

export async function fetchTaskTemplatesV2(): Promise<TaskTemplateV2[]> {
  const { data } = await http.get<{ result?: TaskTemplateV2[] }>(
    '/v2/tasks/templates'
  );

  return data.result ?? [];
}

export async function fetchTaskTemplateV2(
  templateId: string
): Promise<TaskTemplateV2> {
  const { data } = await http.get<TaskTemplateV2>(
    `/v2/tasks/templates/${encodeURIComponent(templateId)}`
  );

  return data;
}

export function updateTaskTemplateV2(
  templateId: string,
  payload: TaskTemplateWriteV2
) {
  return http.patch(
    `/v2/tasks/templates/${encodeURIComponent(templateId)}`,
    payload
  );
}

export function deleteTaskTemplateV2(templateId: string) {
  return http.delete(`/v2/tasks/templates/${encodeURIComponent(templateId)}`);
}

/** Создать задачу по шаблону. Возвращает id новой задачи. */
export async function instantiateTaskTemplateV2(
  templateId: string
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `/v2/tasks/templates/${encodeURIComponent(templateId)}/instantiate`
  );

  return data.id ?? '';
}

/** Сохранить существующую задачу как шаблон. */
export async function saveTaskAsTemplateV2(
  taskId: string,
  payload: SaveAsTemplateV2
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `${task(taskId)}/save-as-template`,
    payload
  );

  return data.id ?? '';
}

/* ---------------------------------------------------------- повторы --- */

/** Все правила повторения. */
export async function fetchRecurrencesV2(): Promise<RecurrenceV2[]> {
  const { data } = await http.get<{ result?: RecurrenceV2[] }>(
    '/v2/tasks/recurrences'
  );

  return data.result ?? [];
}

/** История запусков правила: когда сработало и что породило. */
export async function fetchRecurrenceHistoryV2(
  recurrenceId: string
): Promise<RecurrenceRunV2[]> {
  const { data } = await http.get<{ result?: RecurrenceRunV2[] }>(
    `/v2/tasks/recurrences/${encodeURIComponent(recurrenceId)}/history`
  );

  return data.result ?? [];
}

export function updateRecurrenceV2(
  recurrenceId: string,
  payload: RecurrenceUpdateV2
) {
  return http.patch(
    `/v2/tasks/recurrences/${encodeURIComponent(recurrenceId)}`,
    payload
  );
}

/** Правило повторения конкретной задачи. */
export async function fetchTaskRecurrenceV2(
  taskId: string
): Promise<RecurrenceV2> {
  const { data } = await http.get<RecurrenceV2>(`${task(taskId)}/recurrence`);

  return data;
}

export async function createTaskRecurrenceV2(
  taskId: string,
  payload: RecurrenceCreateV2
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `${task(taskId)}/recurrence`,
    payload
  );

  return data.id ?? '';
}

export function deleteTaskRecurrenceV2(taskId: string) {
  return http.delete(`${task(taskId)}/recurrence`);
}

/* ---------------------------------------------- промежуточные дела --- */

/** Список промежуточных дел — по всем задачам, не по одной. */
export async function fetchIntermediatesV2(): Promise<IntermediateV2[]> {
  const { data } = await http.get<{ result?: IntermediateV2[] }>(
    '/v2/tasks/intermediate'
  );

  return data.result ?? [];
}

/** Моё промежуточное дело по задаче — в том числе завершённое. Нет дела — 404. */
export async function fetchTaskIntermediateV2(
  taskId: string
): Promise<IntermediateV2> {
  const { data } = await http.get<IntermediateV2>(
    `${task(taskId)}/intermediate`
  );

  return data;
}

export async function createIntermediateV2(
  taskId: string,
  payload: IntermediateCreateV2
): Promise<string> {
  const { data } = await http.post<{ id?: string }>(
    `${task(taskId)}/intermediate`,
    payload
  );

  return data.id ?? '';
}

/* Управление таймером. Все четыре отдают обновлённое дело — состояние берём
 * из ответа, а не из локальных догадок. */

export async function pauseIntermediateV2(
  taskId: string
): Promise<IntermediateV2> {
  const { data } = await http.post<IntermediateV2>(
    `${task(taskId)}/intermediate/pause`
  );

  return data;
}

export async function resumeIntermediateV2(
  taskId: string
): Promise<IntermediateV2> {
  const { data } = await http.post<IntermediateV2>(
    `${task(taskId)}/intermediate/resume`
  );

  return data;
}

export async function completeIntermediateV2(
  taskId: string
): Promise<IntermediateV2> {
  const { data } = await http.post<IntermediateV2>(
    `${task(taskId)}/intermediate/complete`
  );

  return data;
}

/** Ручная правка потраченного времени. */
export async function setIntermediateTimeV2(
  taskId: string,
  payload: IntermediateTimeV2
): Promise<IntermediateV2> {
  const { data } = await http.patch<IntermediateV2>(
    `${task(taskId)}/intermediate/time`,
    payload
  );

  return data;
}
